/**
 * Modern Header Component
 * Clean AppBar with breadcrumbs, notifications, and user menu
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Divider,
  Select,
  FormControl,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
// Dark mode icons - temporarily disabled
// import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
// import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { currentBranch as setCurrentBranch, getRequest } from '../../../redux/modules/branch/slice';
import { USER_ROLE_DEFAULT } from '../../../constants/Constant';
// Dark mode temporarily disabled
// import { useThemeMode } from '../../../theme';
import { DRAWER_WIDTH_EXPANDED, DRAWER_WIDTH_COLLAPSED } from '../Sidebar';

const StyledAppBar = styled(AppBar)(({ theme, sidebarwidth }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: 'all 200ms ease',
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${sidebarwidth}px)`,
    marginLeft: sidebarwidth,
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: 64,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

const BranchSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    paddingTop: 8,
    paddingBottom: 8,
    paddingRight: 32,
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  minWidth: 140,
}));

const Header = ({
  onMenuClick,
  isCollapsed = false,
  basePath = '/admin',
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData } = useSelector(state => state.auth);
  const { getRes, currentBranch } = useSelector(state => state.branch);
  const mainHeading = useSelector(state => state.mainHeading?.heading || 'Dashboard');
  // Dark mode temporarily disabled
  // const { mode, toggleTheme } = useThemeMode();

  // Get branch list from API response - memoized to prevent unnecessary re-renders
  const branchList = useMemo(() => getRes?.data || [], [getRes?.data]);

  // Menu states
  const [userAnchor, setUserAnchor] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);

  const sidebarWidth = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  // Fetch branches on mount
  useEffect(() => {
    dispatch(getRequest());
  }, [dispatch]);

  // Auto-select first branch when branches load and no branch is selected
  useEffect(() => {
    if (branchList.length > 0 && !currentBranch) {
      // Check for saved branch in session storage
      const savedBranch = sessionStorage.getItem('defaultBranch');
      if (savedBranch) {
        try {
          const parsed = JSON.parse(savedBranch);
          // Verify the saved branch still exists
          const exists = branchList.find(b => b._id === parsed.id || b._id === parsed._id);
          if (exists) {
            dispatch(setCurrentBranch({ name: exists.name, id: exists._id }));
            return;
          }
        } catch (e) {
          // Invalid saved branch, use first one
        }
      }
      // Default to first branch
      const firstBranch = branchList[0];
      dispatch(setCurrentBranch({ name: firstBranch.name, id: firstBranch._id }));
    }
  }, [branchList, currentBranch, dispatch]);

  const handleUserMenuOpen = (event) => {
    setUserAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserAnchor(null);
  };

  const handleNotifMenuOpen = (event) => {
    setNotifAnchor(event.currentTarget);
  };

  const handleNotifMenuClose = () => {
    setNotifAnchor(null);
  };

  const handleBranchChange = (event) => {
    const selectedBranch = branchList.find(b => b._id === event.target.value);
    if (selectedBranch) {
      // Format as { name, id } for consistency with patient list expectations
      dispatch(setCurrentBranch({ name: selectedBranch.name, id: selectedBranch._id }));
    }
  };

  const handleLogout = () => {
    handleUserMenuClose();
    localStorage.clear();
    navigate('/login');
  };

  const handleEditProfile = () => {
    handleUserMenuClose();
    navigate(`${basePath}/edit-profile`);
  };

  const handleSettings = () => {
    handleUserMenuClose();
    navigate(`${basePath}/settings`);
  };

  const getUserInitials = () => {
    if (userData?.name) {
      return userData.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  return (
    <StyledAppBar position="fixed" sidebarwidth={sidebarWidth}>
      <StyledToolbar>
        {/* Mobile Menu Button */}
        <IconButton
          edge="start"
          color="inherit"
          onClick={onMenuClick}
          sx={{
            mr: 2,
            display: { lg: 'none' },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Branch Name / Title */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'primary.dark',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                letterSpacing: '-0.01em',
              }}
            >
              {currentBranch?.name || 'Select branch'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              {mainHeading}
            </Typography>
          </Box>

          {/* Branch Selector */}
          {branchList?.length > 1 && (
            <FormControl size="small" sx={{ display: { xs: 'none', md: 'flex' }, ml: 2 }}>
              <BranchSelect
                value={currentBranch?.id || ''}
                onChange={handleBranchChange}
                displayEmpty
                IconComponent={KeyboardArrowDownIcon}
              >
                {branchList.map((branch) => (
                  <MenuItem key={branch._id} value={branch._id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </BranchSelect>
            </FormControl>
          )}
        </Box>

        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

          {/* Theme Toggle - temporarily disabled
          <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <IconButton
              onClick={toggleTheme}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
          </Tooltip>
          */}

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton
              onClick={handleNotifMenuOpen}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <Badge badgeContent={0} color="error">
                <NotificationsOutlinedIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          <IconButton
            onClick={handleUserMenuOpen}
            sx={{
              p: 0.5,
              ml: 1,
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {getUserInitials()}
            </Avatar>
          </IconButton>
        </Box>

        {/* Notification Menu */}
        <Menu
          anchorEl={notifAnchor}
          open={Boolean(notifAnchor)}
          onClose={handleNotifMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 280,
              maxHeight: 400,
            },
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Notifications
            </Typography>
          </Box>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No new notifications
            </Typography>
          </Box>
        </Menu>

        {/* User Menu */}
        <Menu
          anchorEl={userAnchor}
          open={Boolean(userAnchor)}
          onClose={handleUserMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: { mt: 1, minWidth: 200 },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {userData?.name || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {userData?.email || ''}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={handleEditProfile}>
            <ListItemIcon>
              <PersonOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Profile</ListItemText>
          </MenuItem>
          {userData?.role === USER_ROLE_DEFAULT.ADMIN && (
            <MenuItem onClick={handleSettings}>
              <ListItemIcon>
                <SettingsOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </StyledToolbar>
    </StyledAppBar>
  );
};

export default Header;
